import axios from 'axios';
import store from '@/store';
import toast from '@/modules/toast/services';
import bugsnag from '@/plugins/bugsnag';
import { i18n } from '@/plugins/vue-i18n';
import isObject from 'lodash/isObject';

export function UnAuthorizedError(message) {
  this.name = '401 Unauthorized';
  this.message = message || 'Default Message';
  this.stack = (new Error()).stack;
}
UnAuthorizedError.prototype = Object.create(Error.prototype);
UnAuthorizedError.prototype.constructor = UnAuthorizedError;

export function SiteMismatchError(message, cmsVariables = {}) {
  this.name = '400 Site mismatch';
  this.message = message || '';
  this.stack = (new Error()).stack;
  this.cmsVariables = cmsVariables;
}
SiteMismatchError.prototype = Object.create(Error.prototype);
SiteMismatchError.prototype.constructor = SiteMismatchError;


function notifyErrorToBugsnag(error) {
  const { config, response } = error;
  bugsnag.notify(error, {
    request: {
      config,
      response,
    },
  });
}

/**
 * example:
 * cmsMessage: "duplicatedValue"
 * cmsMessageVariables: {value: "'asoria@bitban.com'"}
 * @param response
 * @param defaultMessage
 * @returns {VueI18n.TranslateResult|string}
 */
export function formatErrorResponseMessage(response = {}, defaultMessage = '') {
  if (!response.cmsMessage) {
    return defaultMessage;
  }
  const message = `message.error.${response.cmsMessage}`;
  if (response.cmsMessageVariables && isObject(response.cmsMessageVariables)) {
    return i18n.t(message, response.cmsMessageVariables);
  }
  return message;
}

export function catch500(error) {
  if (error.response.status >= 500) {
    // Server errors
    console.debug('API Catch 500!');
    notifyErrorToBugsnag(error);
    toast.notify('message.error.serverError');
    store.dispatch('UPDATE_LOADING_VIEW', false);
    throw error;
  }
}

export function catch400(error) {
  if (error.response.status === 400) {
    const response = error.response.data;

    console.debug('API Catch 400!');
    notifyErrorToBugsnag(error);

    if (isObject(response)
      && response.cmsMessage === 'siteMismatch'
    ) {
      throw new SiteMismatchError(response.message, response.cmsMessageVariables);
    } else {
      // Bad request
      displayErrorMessage(error, 'message.error.badRequest');
      store.dispatch('UPDATE_LOADING_VIEW', false);
      throw error;
    }
  }
}

export function catch401(error) {
  if (error.response.status === 401) {
    // Unauthorized
    console.debug('API Catch 401!');
    // TODO: get config route from router
    if (error.config.url === '/api/config') {
      throw new UnAuthorizedError('Staff is not logged, redirect to login page');
    } else {
      console.debug('Staff session lost, show login modal');
      store.dispatch('authentication/UPDATE_IS_LOGGED', false);
    }
  }
}

export function catch403(error) {
  if (error.response.status === 403) {
    // Forbidden
    console.debug('API Catch 403!, Forbidden operation');
    toast.notify('message.error.forbidden', 5000);
    throw error;
  }
}

export function catch404(error) {
  if (error.response.status === 404) {
    // Not found
    console.debug('API Catch 404!');
    toast.notify('message.error.notFound');
    store.dispatch('UPDATE_LOADING_VIEW', false);
    throw error;
  }
}

export function catch406(error) {
  if (error.response.status === 406) {
    // Not found
    console.debug('API Catch 406!');
    displayErrorMessage(error, 'message.error.notAcceptable');
    store.dispatch('UPDATE_LOADING_VIEW', false);
    throw error;
  }
}

export function catchNetworkError(error) {
  if (!error.response) {
    console.debug('Network error');
    toast.notify('message.error.serverError');
    throw error;
  }
}

export function displayErrorMessage(error, defaultMessage) {
  const data = error.response.data || {};
  const dialog = data.cmsDialog;
  if (dialog) {
    store.dispatch('dialog/CREATE', dialog);
  } else {
    toast.notify(formatErrorResponseMessage(data, defaultMessage));
  }
}

export default function catchError(error, config = {}) {
  // Throw error on axios cancel
  if (axios.isCancel(error)) {
    throw error;
  } else {
    catchNetworkError(error);
    catch400(error);
    catch401(error);
    catch403(error);
    catch404(error);
    catch406(error);
    catch500(error);
  }
}
